import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Landmark, FileCheck2, ShieldCheck, Download, ArrowRight, Printer, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { API } from "@/lib/api";

const openBlankForm = () => window.open(`${API}/forms/blank-pdf`, "_blank", "noopener");
const openBlankFormDocx = () => window.open(`${API}/forms/blank-docx`, "_blank", "noopener");

const FALLBACK = {
  site_name: "Cheltenham Township",
  site_tagline: "Tax Filing Portal",
  badge: "Montgomery County, Pennsylvania",
  title: "File your Mercantile & Business Privilege Tax online",
  subtitle:
    "The official portal for Cheltenham Township merchants. Complete your return, let us calculate every line automatically, and download a signed PDF copy in minutes.",
  cta_primary: "Start a Filing",
  cta_secondary: "I already have an account",
  features: [
    { title: "Guided Return", body: "Fill the 2025 final return and 2026 estimate. Wholesale, retail, and business privilege lines are computed for you." },
    { title: "Auto-Calculated", body: "Tax, penalty, interest, and license fees follow the township's official rates — no math required." },
    { title: "Instant PDF", body: "Download a clean, official copy of your filing. The finance office is notified automatically." },
  ],
  footer:
    "Cheltenham Township Finance & Tax Office · 8230 Old York Road, Elkins Park, PA 19027-1589. Questions? Contact the Finance Office before the April 18, 2026 deadline.",
};

const FEATURE_ICONS = [FileCheck2, ShieldCheck, Download];

const SECTION_META = [
  { type: "news", key: "news_enabled", id: "news", label: "Tax News", heading: "Tax News & Updates" },
  { type: "blog", key: "blog_enabled", id: "blog", label: "Blog", heading: "Blog Updates" },
  { type: "event", key: "events_enabled", id: "events", label: "Events", heading: "Upcoming Events" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [c, setC] = useState(FALLBACK);
  const [content, setContent] = useState({ sections: {}, news: [], blog: [], event: [] });
  const [signupEnabled, setSignupEnabled] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings/landing").catch(() => ({ data: {} })),
      api.get("/settings/brand").catch(() => ({ data: {} })),
      api.get("/content/public").catch(() => ({ data: { sections: {}, news: [], blog: [], event: [] } })),
      api.get("/settings/access").catch(() => ({ data: { signup_enabled: false } })),
    ]).then(([l, b, ct, ac]) => {
      setC({ ...FALLBACK, ...l.data, ...b.data });
      setContent(ct.data);
      setSignupEnabled(!!ac.data.signup_enabled);
    });
  }, []);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-[#1E3A8A] flex items-center justify-center">
              <Landmark className="w-5 h-5 text-white" />
            </span>
            <div className="leading-tight">
              <p className="font-heading font-semibold text-slate-900 text-sm">{c.site_name}</p>
              <p className="text-xs text-slate-500">{c.site_tagline}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <nav className="hidden md:flex items-center gap-1 mr-2">
              {SECTION_META.filter((m) => content.sections?.[m.key]).map((m) => (
                <a
                  key={m.id}
                  href={`#${m.id}`}
                  data-testid={`nav-${m.id}`}
                  className="text-sm text-slate-600 hover:text-[#1E3A8A] px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                >
                  {m.label}
                </a>
              ))}
            </nav>
            <Button variant="ghost" onClick={openBlankForm} data-testid="landing-paper-form-btn" className="hidden sm:inline-flex">
              <Printer className="w-4 h-4 mr-2" /> Paper Form
            </Button>
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-btn">
              Sign In
            </Button>
            {signupEnabled && (
              <Button
                onClick={() => navigate("/register")}
                data-testid="landing-register-btn"
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              >
                Register
              </Button>
            )}
          </div>
        </div>
      </header>

      <section className="civic-hero">
        <div className="max-w-6xl mx-auto px-6 py-24 sm:py-32">
          <div className="max-w-2xl">
            <span className="inline-block text-xs font-medium uppercase tracking-widest text-blue-200 border border-blue-300/40 rounded-full px-3 py-1 mb-6">
              {c.badge}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold text-white leading-[1.1]">
              {c.title}
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-xl">
              {c.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => navigate(signupEnabled ? "/register" : "/login")}
                data-testid="landing-cta-start"
                className="bg-white text-[#1E3A8A] hover:bg-slate-100 font-semibold"
              >
                {c.cta_primary} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/login")}
                className="border-white/50 text-white hover:bg-white/10 bg-transparent"
              >
                {c.cta_secondary}
              </Button>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-blue-100">
              <button
                onClick={openBlankForm}
                data-testid="hero-paper-form-link"
                className="inline-flex items-center gap-2 hover:text-white underline underline-offset-4 transition-colors"
              >
                <Download className="w-4 h-4" /> Prefer to mail it in? Download the paper form (PDF)
              </button>
              <button
                onClick={openBlankFormDocx}
                data-testid="hero-paper-form-docx-link"
                className="inline-flex items-center gap-2 hover:text-white underline underline-offset-4 transition-colors"
              >
                <FileText className="w-4 h-4" /> Word (.docx)
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {c.features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || FileCheck2;
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-md p-8 hover:shadow-sm transition-colors">
                <span className="w-11 h-11 rounded-md bg-blue-50 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#1E3A8A]" />
                </span>
                <h3 className="font-heading text-xl font-medium text-slate-900">{f.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
        {SECTION_META.filter((m) => content.sections?.[m.key]).map((m) => (
          <section key={m.id} id={m.id} className="mt-16 scroll-mt-24" data-testid={`section-${m.id}`}>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 mb-6">
              {m.heading}
            </h2>
            {content[m.type] && content[m.type].length ? (
              <div className="grid md:grid-cols-3 gap-6">
                {content[m.type].map((it) => (
                  <article
                    key={it.id}
                    data-testid={`landing-${m.type}-item`}
                    className="bg-white border border-slate-200 rounded-md overflow-hidden hover:shadow-sm transition-colors"
                  >
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="w-full h-40 object-cover" />
                    ) : null}
                    <div className="p-6">
                      {m.type === "event" && it.event_date ? (
                        <p className="text-xs font-medium text-[#1E3A8A] mb-1">
                          {new Date(it.event_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                        </p>
                      ) : null}
                      <h3 className="font-heading text-lg font-medium text-slate-900">{it.title}</h3>
                      <p className="mt-2 text-slate-600 text-sm leading-relaxed whitespace-pre-line line-clamp-4">
                        {it.body}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No updates yet — check back soon.</p>
            )}
          </section>
        ))}

        <div className="mt-16 border-t border-slate-200 pt-8 text-sm text-slate-500">
          <p>{c.footer}</p>
          <p className="mt-2">
            <Link to="/login" className="text-[#1E3A8A] hover:underline">Sign in</Link> to view or
            download your past filings.
          </p>
        </div>
      </section>
    </div>
  );
}
